import React from "react";
import { motion } from "framer-motion";
import { Brain, Bone, Activity, Stethoscope, ArrowUpRight } from "lucide-react";

const areas = [
  {
    key: "psychiatry",
    title: "Psychiatry",
    description:
      "Evidence-led formulations for anxiety, depression, sleep and mood disorders — co-developed with practising psychiatrists.",
    icon: Brain,
    span: "md:col-span-8",
    tone: "light",
    tagline: "Restoring balance, gently.",
    products: ["Antidepressants", "Anxiolytics", "Mood Stabilizers", "Sleep Aids"],
    accent: "from-sky-100/80 to-white",
  },
  {
    key: "orthopedics",
    title: "Orthopedics",
    description:
      "Calcium, NSAIDs, muscle relaxants and joint-care molecules built for orthopaedic practice.",
    icon: Bone,
    span: "md:col-span-4",
    tone: "dark",
    tagline: "Built for strength.",
    products: ["NSAIDs", "Calcium Range", "Muscle Relaxants"],
  },
  {
    key: "neurology",
    title: "Neurology",
    description:
      "Targeted neuro-protective and neuropathic-pain therapies — precision-designed for clinical predictability.",
    icon: Activity,
    span: "md:col-span-6",
    tone: "light",
    tagline: "Precision for the nervous system.",
    products: ["Neuropathic Pain", "Anti-Epileptics", "Neuroprotectives"],
  },
  {
    key: "general-medicine",
    title: "General Medicine",
    description:
      "A reliable everyday arsenal — antibiotics, antacids, multivitamins and PCD-friendly OTC essentials.",
    icon: Stethoscope,
    span: "md:col-span-6",
    tone: "light",
    tagline: "Care, made universal.",
    products: ["Antibiotics", "Antacids", "Multivitamins", "Antipyretics"],
  },
];

export default function TherapeuticAreas() {
  return (
    <section
      id="therapeutic-areas"
      data-testid="therapeutic-areas-section"
      className="py-24 md:py-32 bg-white border-y border-[var(--mp-border)]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="overline mb-3">Therapeutic Areas</div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium leading-[1.05]">
              Four focused specialties.
              <br />
              <span className="text-[var(--mp-brand)]">One commitment to clinical truth.</span>
            </h2>
          </div>
          <p className="md:max-w-sm text-[var(--mp-text-2)] leading-relaxed">
            We don't try to make everything for everyone. We make the right medicines for the
            specialties our team understands deepest.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {areas.map((a, i) => {
            const Icon = a.icon;
            const isDark = a.tone === "dark";
            return (
              <motion.article
                key={a.key}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                data-testid={`therapeutic-card-${a.key}`}
                className={`relative col-span-12 ${a.span} rounded-3xl p-8 md:p-10 border overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                  isDark
                    ? "bg-[var(--mp-dark)] border-[var(--mp-border-dark)] text-white"
                    : "bg-white border-[var(--mp-border)]"
                }`}
              >
                {!isDark && (
                  <div
                    aria-hidden
                    className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gradient-to-br from-sky-100/70 to-transparent blur-2xl"
                  />
                )}

                <div className="flex items-start justify-between relative">
                  <div
                    className={`inline-flex items-center justify-center h-12 w-12 rounded-xl ${
                      isDark ? "bg-white/10 text-white" : "bg-[var(--mp-accent-surface)] text-[var(--mp-brand)]"
                    }`}
                  >
                    <Icon className="h-6 w-6" strokeWidth={1.6} />
                  </div>
                  <span
                    className={`font-mono-display text-[10px] tracking-[0.25em] uppercase ${
                      isDark ? "text-slate-400" : "text-[var(--mp-text-2)]"
                    }`}
                  >
                    0{i + 1} / 04
                  </span>
                </div>

                <div className="mt-12 relative">
                  <h3
                    className={`font-display text-3xl md:text-4xl tracking-tighter font-medium leading-tight ${
                      isDark ? "text-white" : "text-[var(--mp-text)]"
                    }`}
                  >
                    {a.title}
                  </h3>
                  <p
                    className={`mt-2 italic font-display text-sm ${
                      isDark ? "text-slate-300" : "text-[var(--mp-brand)]"
                    }`}
                  >
                    {a.tagline}
                  </p>
                  <p
                    className={`mt-5 leading-relaxed text-[15px] max-w-md ${
                      isDark ? "text-slate-300" : "text-[var(--mp-text-2)]"
                    }`}
                  >
                    {a.description}
                  </p>

                  <div className="mt-7 flex flex-wrap gap-2">
                    {a.products.map((p) => (
                      <span
                        key={p}
                        className={`px-3 py-1 text-xs rounded-full border ${
                          isDark
                            ? "border-white/20 text-slate-200"
                            : "border-[var(--mp-border)] text-[var(--mp-text-2)] bg-[var(--mp-bg)]"
                        }`}
                      >
                        {p}
                      </span>
                    ))}
                  </div>

                  <a
                    href="#products"
                    data-testid={`therapeutic-cta-${a.key}`}
                    className={`mt-8 inline-flex items-center gap-1.5 text-sm font-medium group ${
                      isDark ? "text-white" : "text-[var(--mp-text)]"
                    }`}
                  >
                    See products
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
